<script setup lang="ts">
import type { FieldData } from '~~/server/api/forms/form'
import { FormKitNode } from '@formkit/core'
import { getValidationMessages } from '@formkit/validation'
import { nextTick } from 'vue'

export type FormKitCustomSelectOption = {
    [key: string]: string
}

export interface FormKitCustomSelectProps {
    data: Pick<
        FieldData,
        | 'list_options'
            | 'defaultValue'
            | 'label'
            | 'name'
            | 'help'
            | 'validations'
            | 'validation_messages'
    >;
    showLabel?: boolean;
}

const props = defineProps<FormKitCustomSelectProps>()
const emit = defineEmits<{ (e: 'update', value: any): void }>()

const expanded = ref(false)
const current = ref<any>(props.data.defaultValue)
const currentIndex = ref(-1)

const buttonEle = ref<HTMLElement | null>(null)
const wrapperEle = ref<HTMLElement | null>(null)
const optionsEle = ref<HTMLElement[]>([])

const input = ref()
const messages = ref<string[]>([])

const optionsCount = computed(() =>
    props.data.list_options ? Object.keys(props.data.list_options).length : 0
)

const isInvalid = computed(() => {
    const node: FormKitNode = input.value?.node
    if (!node) return false
    return (
        !node.context?.state?.valid && node.context?.state?.validationVisible
    )
})

const currentValueText = computed(() => {
    if (!props.data.list_options || !current.value) return ''
    return String(props.data.list_options[current.value] ?? '')
})

const buttonAriaLabel = computed(() => {
    return currentValueText.value
        ? `${props.data.label}: ${currentValueText.value}`
        : props.data.label || ''
})

const openDropdown = async (index = 0) => {
    expanded.value = true
    currentIndex.value = index

    await nextTick()
    optionsEle.value[currentIndex.value]?.focus()
}

const closeDropdown = () => {
    expanded.value = false
    currentIndex.value = -1
}

const selectOption = (key: any) => {
    current.value = key
    emit('update', key)

    closeDropdown()
    buttonEle.value?.focus()
}

const handleButtonKeydown = (e: KeyboardEvent) => {
    switch (e.key) {
    case 'ArrowDown':
        e.preventDefault()
        openDropdown(0)
        break

    case 'ArrowUp':
        e.preventDefault()
        openDropdown(optionsCount.value - 1)
        break

    case 'Enter':
    case ' ':
        e.preventDefault()
        expanded.value ? closeDropdown() : openDropdown(0)
        break

    case 'Tab':
        closeDropdown()
        break

    case 'Escape':
        closeDropdown()
        break
    }
}

const handleOptionKeydown = (e: KeyboardEvent, idx: number) => {
    switch (e.key) {
    case 'ArrowDown':
        e.preventDefault()
        currentIndex.value = (idx + 1) % optionsCount.value
        nextTick(() => optionsEle.value[currentIndex.value]?.focus())
        break

    case 'ArrowUp':
        e.preventDefault()
        currentIndex.value = idx === 0 ? optionsCount.value - 1 : idx - 1
        nextTick(() => optionsEle.value[currentIndex.value]?.focus())
        break

    case 'Enter': {
        e.preventDefault()
        const optionKey = Object.keys(props.data.list_options!)[idx] as string
        selectOption(optionKey)
        break
    }

    case 'Escape':
        closeDropdown()
        buttonEle.value?.focus()
        break

    case 'Tab':
        closeDropdown()
        break
    }
}

const handleClickOutside = (e: Event) => {
    if (!wrapperEle.value?.contains(e.target as Node)) {
        closeDropdown()
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)

    const node: FormKitNode = input.value?.node
    const validations = getValidationMessages(node)

    messages.value = []
    validations.forEach((inputMessages) => {
        messages.value.push(
            ...inputMessages.map(() => {
                return (
                    props.data.validation_messages?.['required'] ||
                    `${props.data.label} je povinný údaj`
                )
            })
        )
    })
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>
<template>
  <div
    :id="props.data.name"
    ref="wrapperEle"
    class="simple-select-wrapper position-relative rounded-3"
  >
    <FormKit
      ref="input"
      v-model="current"
      type="hidden"
      :name="props.data.name"
      :validation="props.data.validations"
    />

    <!-- BUTTON -->
    <button
      ref="buttonEle"
      :aria-expanded="expanded"
      aria-haspopup="listbox"
      :aria-label="buttonAriaLabel"
      class="formkit-input simple-select px-3 position-relative d-flex align-items-center justify-content-between pt-0 pb-0 stretched-link"
      :class="{
        expanded: expanded || Boolean(current),
        'border border-secondary': isInvalid,
      }"
      type="button"
      @click="expanded ? closeDropdown() : openDropdown(0)"
      @keydown="handleButtonKeydown"
    >
      <div
        v-if="props.showLabel"
        class="formkit-label position-absolute fw-normal transition"
        :class="{ open: expanded || Boolean(current) }"
      >
        {{ props.data.label
        }}<template v-if="props.data.validations?.includes('required')">
          *
        </template>
      </div>

      <span
        class="d-flex align-items-center text-start text-nowrap pe-none"
      >
        <span
          class="text-black text-nowrap overflow-hidden text-truncate"
        >
          {{ props.data.list_options?.[current] || "-" }}
        </span>
      </span>

      <DropdownDecor />
    </button>

    <!-- ERRORS -->
    <ul
      v-if="isInvalid"
      class="formkit-messages"
    >
      <li
        v-for="(message, index) in messages"
        :key="index"
        class="formkit-message"
      >
        {{ message }}
      </li>
    </ul>

    <!-- DROPDOWN -->
    <Card
      :class="[
        expanded ? 'select-dropdown-visible' : 'select-dropdown-hidden',
        'roudned-2 px-1 py-3 border border-bcolor box-shadow-4 mt-1 position-absolute start-0 end-0 z-index-5',
      ]"
      background-color="white"
      box-shadow="4"
    >
      <ul
        role="listbox"
        class="select-menu-list"
      >
        <li
          v-for="(name, key, idx) in (props.data.list_options as unknown as Record<string, string>)"
          :key="String(key)"
          role="option"
          :aria-selected="current === key"
        >
          <button
            :ref="(el) => { if (el) optionsEle[idx] = el as HTMLElement }"
            type="button"
            class="formkit-custom-select-item text-black d-block w-100 text-start p-2 fs-smaller border-0 rounded-3"
            :tabindex="currentIndex === idx ? 0 : -1"
            @click="selectOption(key)"
            @keydown="(e) => handleOptionKeydown(e, idx)"
          >
            {{ name }}
          </button>
        </li>
      </ul>
    </Card>

    <div
      v-if="props.data.help"
      class="formkit-help"
    >
      {{ props.data.help }}
    </div>
  </div>
</template>
<style scoped lang="scss">
@import "./FormKitCustomSelect.scss";

// Accessibility: Use visibility instead of display:none to allow focus management
.select-dropdown-hidden {
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
}

.select-dropdown-visible {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
}
</style>
